import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppDatePicker from '../common/AppDatePicker';
import AppTimePicker from '../common/AppTimePicker';
import DeleteConfirmModal from '../layouts/DeleteConfirmModal';
import formatDate from '../../utils/formatDate';
import { downloadDietPDF, downloadDietPDFTamil, printDiet } from '../../utils/dietPdf';
import { translateDietToTamil } from '../../utils/tamilTranslate';
import {
    getDietTemplates,
    createPatientDiet,
    updatePatientDiet,
    deletePatientDiet,
    getPatientDiets,
    getAllPatientDiets,
    deleteDietTemplate
} from '../../actions/dietActions';
import { getAllStaff } from '../../actions/staffActions';
import {
    clearDietCreated,
    clearDietUpdated,
    clearDietDeleted,
    clearDietError
} from '../../slices/dietSlice';

const safeSlots = (slots) => {
    if (Array.isArray(slots)) return slots;
    if (typeof slots === 'string') {
        try { return JSON.parse(slots); } catch (e) { return []; }
    }
    return [];
};

export default function PatientDietSection({ patient }) {
    const dispatch = useDispatch();
    const patientId = patient?._id || patient?.id;

    const { user } = useSelector(state => state.authState);
    const { staffList } = useSelector(state => state.staffState);
    const {
        templates = [],
        diets = [],
        allPatientDiets = [],
        loading,
        error,
        isDietCreated,
        isDietUpdated,
        isDietDeleted
    } = useSelector(state => state.dietState);

    // Modals & Panels
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [previewDiet, setPreviewDiet] = useState(null);
    const [editingDiet, setEditingDiet] = useState(null);
    const [showSearchPastModal, setShowSearchPastModal] = useState(false);
    const [pastSearchTerm, setPastSearchTerm] = useState('');
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });
    const [deleteTemplateModal, setDeleteTemplateModal] = useState({ show: false, id: null, name: '' });
    const [deletedDietIds, setDeletedDietIds] = useState([]);
    const [deletedTemplateIds, setDeletedTemplateIds] = useState([]);
    const [hasFetchedDiets, setHasFetchedDiets] = useState(false);

    // Single source of truth for patient diets:
    // Once getPatientDiets completes, strictly use Redux `diets`.
    // Prior to fetch, fallback to patient.diets.
    // In both cases, filter out any deleted ID in local state so deletion is instantaneous.
    const displayedDiets = useMemo(() => {
        const sourceList = hasFetchedDiets
            ? (diets || [])
            : ((diets && diets.length > 0) ? diets : (Array.isArray(patient?.diets) ? patient.diets : []));

        return (sourceList || []).filter(
            d => !deletedDietIds.includes(String(d._id || d.id))
        );
    }, [hasFetchedDiets, diets, patient?.diets, deletedDietIds]);

    const availableTemplates = useMemo(() => {
        return (templates || []).filter(t => !deletedTemplateIds.includes(String(t._id || t.id)));
    }, [templates, deletedTemplateIds]);

    // Available Doctors
    const doctors = (staffList || []).filter(
        s => s.designation && (
            s.designation.toLowerCase().includes('doctor') ||
            s.designation.toLowerCase().includes('bnys') ||
            s.designation.toLowerCase().includes('physician')
        )
    );
    const defaultDoctor = doctors.length > 0 ? doctors[0].name : (user?.name || '');

    // Form State - Digital Diet
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [dietTitle, setDietTitle] = useState('');
    const [condition, setCondition] = useState('');
    const [doctor, setDoctor] = useState(defaultDoctor);
    const [doctorDesignation, setDoctorDesignation] = useState('BNYS');
    const [activeWhatsAppId, setActiveWhatsAppId] = useState(null);
    const [dietDate, setDietDate] = useState(new Date().toISOString().split('T')[0]);
    const [guidelines, setGuidelines] = useState('');
    const [saveAsTemplate, setSaveAsTemplate] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');

    // Auto-select doctor if only 1 doctor exists
    useEffect(() => {
        if (!doctor && doctors.length === 1) {
            handleDoctorChange(doctors[0].name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctors]);

    useEffect(() => {
        if (!uploadDoctor && doctors.length === 1) {
            setUploadDoctor(doctors[0].name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctors]);

    const handleDoctorChange = (selectedName) => {
        setDoctor(selectedName);
        const match = (staffList || []).find(s => s.name === selectedName);
        if (match) {
            let qual = match.qualification;
            if (!qual && match.designation && match.designation.includes('(')) {
                qual = match.designation.split('(')[1].replace(')', '').trim();
            }
            setDoctorDesignation(qual || 'BNYS');
        } else {
            setDoctorDesignation('BNYS');
        }
    };

    // Default starting slots when put from scratch - completely blank
    const initialSlots = useMemo(() => [
        { id: 's-1', time: '', title: '', items: '', instructions: '' }
    ], []);
    const [slots, setSlots] = useState(initialSlots);
    const [isTranslating, setIsTranslating] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Form State - Upload File
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadDoctor, setUploadDoctor] = useState(defaultDoctor);
    const [uploadDate, setUploadDate] = useState(new Date().toISOString().split('T')[0]);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadNotes, setUploadNotes] = useState('');

    const resetForms = useCallback(() => {
        setEditingDiet(null);
        setSelectedTemplateId('');
        setDietTitle('');
        setCondition('');
        setDoctor(defaultDoctor);
        setDoctorDesignation('BNYS');
        setDietDate(new Date().toISOString().split('T')[0]);
        setSlots(initialSlots);
        setGuidelines('');
        setSaveAsTemplate(false);
        setNewTemplateName('');

        setUploadTitle('');
        setUploadDoctor(defaultDoctor);
        setUploadDate(new Date().toISOString().split('T')[0]);
        setUploadFile(null);
        setUploadNotes('');
    }, [defaultDoctor, initialSlots]);

    // Load templates, staff, and patient diets on mount
    useEffect(() => {
        dispatch(getDietTemplates());
        dispatch(getAllPatientDiets());
        dispatch(getAllStaff());
        if (patientId) {
            dispatch(getPatientDiets(patientId)).then(() => {
                setHasFetchedDiets(true);
            });
        }
    }, [dispatch, patientId]);

    // Handle feedback notifications
    useEffect(() => {
        if (isDietCreated) {
            toast.success('Diet plan saved successfully!');
            dispatch(clearDietCreated());
            setShowCreateModal(false);
            setShowUploadModal(false);
            resetForms();
            if (patientId) dispatch(getPatientDiets(patientId));
        }
        if (isDietUpdated) {
            toast.success('Diet plan updated successfully!');
            dispatch(clearDietUpdated());
            setShowCreateModal(false);
            resetForms();
            if (patientId) dispatch(getPatientDiets(patientId));
        }
        if (isDietDeleted) {
            dispatch(clearDietDeleted());
            setDeleteModal({ show: false, id: null, name: '' });
        }
        if (error) {
            toast.error(error);
            dispatch(clearDietError());
        }
    }, [isDietCreated, isDietUpdated, isDietDeleted, error, dispatch, patientId, resetForms]);

    const handleEditDiet = (dietItem) => {
        setEditingDiet(dietItem);
        setSelectedTemplateId('');
        setDietTitle(dietItem.title || '');
        setCondition(dietItem.condition || '');
        setDoctor(dietItem.doctor || defaultDoctor);
        const match = (staffList || []).find(s => s.name === dietItem.doctor);
        if (match && (match.qualification || match.designation)) {
            setDoctorDesignation(match.qualification || match.designation);
        } else {
            setDoctorDesignation(dietItem.doctorDesignation || 'BNYS');
        }
        if (dietItem.date) {
            try {
                const datePart = new Date(dietItem.date).toISOString().split('T')[0];
                setDietDate(datePart);
            } catch (e) {
                setDietDate(new Date().toISOString().split('T')[0]);
            }
        }
        setGuidelines(dietItem.guidelines || '');
        const parsedSlots = safeSlots(dietItem.slots);
        if (parsedSlots.length > 0) {
            setSlots(parsedSlots.map((s, idx) => ({
                id: s.id || `slot-${idx + 1}-${Date.now()}`,
                time: s.time || '',
                title: s.title || `Slot ${idx + 1}`,
                items: s.items || '',
                instructions: s.instructions || ''
            })));
        } else {
            setSlots(initialSlots);
        }
        setSaveAsTemplate(false);
        setNewTemplateName('');
        setShowCreateModal(true);
    };

    const handleImportPastDiet = (pastDiet) => {
        if (!pastDiet) return;
        setDietTitle(pastDiet.title || '');
        setCondition(pastDiet.condition || '');
        setGuidelines(pastDiet.guidelines || '');
        const parsedSlots = safeSlots(pastDiet.slots);
        if (parsedSlots.length > 0) {
            setSlots(parsedSlots.map((s, idx) => ({
                id: s.id || `slot-${idx + 1}-${Date.now()}`,
                time: s.time || '',
                title: s.title || `Slot ${idx + 1}`,
                items: s.items || '',
                instructions: s.instructions || ''
            })));
        }
        setShowSearchPastModal(false);
        toast.info(`Loaded diet schedule from "${pastDiet.title}"!`);
    };

    // When template selection changes -> Populate slots and guidelines
    const handleTemplateSelect = (val) => {
        setSelectedTemplateId(val);
        if (!val) {
            setDietTitle('');
            setCondition('');
            setGuidelines('');
            setSlots(initialSlots);
            return;
        }

        let found = null;
        if (val.startsWith('template:')) {
            const rawId = val.replace('template:', '');
            found = templates.find(t => String(t._id || t.id) === String(rawId));
        } else if (val.startsWith('past:')) {
            const rawId = val.replace('past:', '');
            found = allPatientDiets.find(d => String(d._id || d.id) === String(rawId));
        } else {
            found = templates.find(t => String(t._id || t.id) === String(val)) ||
                    allPatientDiets.find(d => String(d._id || d.id) === String(val));
        }

        if (found) {
            setDietTitle(found.name || found.title || '');
            setCondition(found.condition || '');
            setGuidelines(found.guidelines || found.notes || '');
            const parsedSlots = safeSlots(found.slots);
            if (parsedSlots.length > 0) {
                setSlots(parsedSlots.map((s, idx) => ({
                    id: s.id || `slot-${idx + 1}-${Date.now()}`,
                    time: s.time || '',
                    title: s.title || `Slot ${idx + 1}`,
                    items: s.items || '',
                    instructions: s.instructions || ''
                })));
            }
        }
    };

    const handleConfirmDeleteTemplate = async () => {
        if (deleteTemplateModal.id) {
            const templateId = String(deleteTemplateModal.id);
            setDeletedTemplateIds(prev => [...prev, templateId]);
            if (String(selectedTemplateId) === templateId) {
                setSelectedTemplateId('');
                setDietTitle('');
                setCondition('');
                setGuidelines('');
                setSlots(initialSlots);
            }
            setDeleteTemplateModal({ show: false, id: null, name: '' });
            const result = await dispatch(deleteDietTemplate(templateId));
            if (result?.success) {
                toast.success(`Common diet template "${deleteTemplateModal.name}" deleted!`);
                dispatch(getDietTemplates());
            } else {
                setDeletedTemplateIds(prev => prev.filter(id => id !== templateId));
                toast.error(result?.error || 'Failed to delete template');
            }
        }
    };

    // Slot Editing Handlers
    const handleSlotChange = (index, field, value) => {
        setSlots(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleAddSlot = () => {
        setSlots(prev => [
            ...prev,
            {
                id: `slot-${Date.now()}`,
                time: '',
                title: '',
                items: '',
                instructions: ''
            }
        ]);
    };

    const handleRemoveSlot = (index) => {
        if (slots.length <= 1) {
            toast.warn('At least one meal slot is required');
            return;
        }
        setSlots(prev => prev.filter((_, i) => i !== index));
    };

    // Submit Digital Diet
    const handleSaveDigitalDiet = async () => {
        if (!dietTitle.trim()) {
            toast.error('Please enter a diet title or condition');
            return;
        }
        if (!doctor.trim()) {
            toast.error('Please select the prescribing doctor');
            return;
        }

        const payload = {
            title: dietTitle.trim(),
            condition: condition.trim(),
            doctor: doctor.trim(),
            doctorDesignation: (doctorDesignation || 'BNYS').trim(),
            date: dietDate,
            dietType: 'digital',
            slots: slots,
            guidelines: guidelines.trim(),
            saveAsTemplate,
            templateName: newTemplateName.trim() || dietTitle.trim()
        };

        if (editingDiet) {
            const dietId = editingDiet._id || editingDiet.id;
            const result = await dispatch(updatePatientDiet(dietId, payload));
            if (result?.success) {
                if (saveAsTemplate) dispatch(getDietTemplates());
            }
        } else {
            const result = await dispatch(createPatientDiet(patientId, payload));
            if (result?.success) {
                // Also refresh templates list if user checked save as template
                if (saveAsTemplate) dispatch(getDietTemplates());
            }
        }
    };

    // Submit File Upload
    const handleSaveFileUpload = async () => {
        if (!uploadFile) {
            toast.error('Please choose a PDF or image file');
            return;
        }
        if (!uploadTitle.trim()) {
            toast.error('Please enter a diet chart title');
            return;
        }

        const formData = new FormData();
        formData.append('title', uploadTitle.trim());
        formData.append('doctor', uploadDoctor.trim());
        formData.append('date', uploadDate);
        formData.append('dietType', 'file');
        formData.append('notes', uploadNotes.trim());
        formData.append('dietFile', uploadFile);

        dispatch(createPatientDiet(patientId, formData));
    };

    // 🔹 Auto-Translate Digital Diet Form Content to Tamil
    const handleTranslateFormToTamil = async () => {
        const hasContent = dietTitle.trim() || condition.trim() || guidelines.trim() || slots.some(s => s.title.trim() || s.items.trim());
        if (!hasContent) {
            toast.warn('Please enter some diet details or select a template first');
            return;
        }

        setIsTranslating(true);
        try {
            const translated = await translateDietToTamil({
                title: dietTitle,
                condition,
                slots,
                guidelines
            });

            if (translated) {
                if (translated.title) setDietTitle(translated.title);
                if (translated.condition) setCondition(translated.condition);
                if (Array.isArray(translated.slots) && translated.slots.length > 0) {
                    setSlots(translated.slots);
                }
                if (translated.guidelines) setGuidelines(translated.guidelines);
                toast.success('Diet translated to Tamil! You can review or edit before saving.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to translate diet to Tamil. Please try again.');
        } finally {
            setIsTranslating(false);
        }
    };

    // 🔹 Download High-Quality Tamil PDF (keeps patient details in English)
    const handleDownloadTamil = async (dietToDownload) => {
        try {
            setDownloadingPdf(true);
            toast.info('Generating Tamil Diet PDF with English patient details...');
            await downloadDietPDFTamil(dietToDownload, patient);
            toast.success('Tamil Diet PDF downloaded successfully!');
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate Tamil PDF. Please try again.');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // 🔹 Print / Save as PDF
    const handlePrintDiet = async (dietToPrint, lang = 'en') => {
        try {
            await printDiet(dietToPrint, patient, lang);
        } catch (err) {
            console.error(err);
            toast.error('Failed to open print view.');
        }
    };

    // 🔹 WhatsApp Share Link (English or Tamil)
    const handleSendWhatsApp = (diet, language = 'en') => {
        const baseUrl = window.location.origin;
        const publicDietLink = `${baseUrl}/diet/view/${diet.publicToken || diet._id || diet.id}${language === 'ta' ? '?lang=ta' : ''}`;
        const patientName = patient?.name || 'Valued Patient';
        const docPrefix = diet.doctor ? (diet.doctor.startsWith('Dr') ? diet.doctor : `Dr. ${diet.doctor}`) : 'Green Naturopathy Team';
        const docDegree = diet.doctorDesignation ? ` (${diet.doctorDesignation})` : (docPrefix.includes('BNYS') ? '' : ', BNYS');
        const docFullName = `${docPrefix}${docDegree}`;
        const conditionName = diet.condition || diet.title || 'Holistic Diet Plan';

        let messageBody = '';
        if (language === 'ta') {
            messageBody = `வணக்கம் ${patientName},\n\n${docFullName} (Green Naturopathy) உங்களுக்கான உணவு முறை மற்றும் ஊட்டச்சத்து அட்டவணையை (${conditionName}) பரிந்துரைத்துள்ளார்.\n\nமுழு உணவு அட்டவணையை காண லிங்க்:\n${publicDietLink}\n\n"உணவே மருந்து, மருந்தே உணவு"\nகிரீன் நேச்சுரோபதி மருத்துவமனை`;
        } else {
            messageBody = `Dear ${patientName},\n\n${docFullName} from Green Naturopathy has prescribed your personalized Diet & Nutrition Plan (${conditionName}).\n\nView & download your official diet chart here:\n${publicDietLink}\n\n"Let food be thy medicine and medicine be thy food."\nGreen Naturopathy Holistic Care`;
        }

        const encoded = encodeURIComponent(messageBody);

        let cleanPhone = String(patient?.phone || '').replace(/[^0-9]/g, '');
        if (cleanPhone.startsWith('0') && cleanPhone.length === 11) {
            cleanPhone = '91' + cleanPhone.slice(1);
        } else if (cleanPhone.length === 10) {
            cleanPhone = '91' + cleanPhone;
        }

        const whatsappUrl = cleanPhone
            ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encoded}`
            : `https://api.whatsapp.com/send?text=${encoded}`;

        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    // Delete Diet
    const confirmDeleteDiet = async () => {
        if (deleteModal.id) {
            const targetId = String(deleteModal.id);
            // Instantly remove from view
            setDeletedDietIds(prev => [...prev, targetId]);
            if (previewDiet && String(previewDiet._id || previewDiet.id) === targetId) {
                setPreviewDiet(null);
            }
            if (editingDiet && String(editingDiet._id || editingDiet.id) === targetId) {
                resetForms();
                setShowCreateModal(false);
            }
            setDeleteModal({ show: false, id: null, name: '' });

            const result = await dispatch(deletePatientDiet(targetId));
            if (result?.success) {
                toast.success('Diet plan deleted successfully!');
                if (patientId) dispatch(getPatientDiets(patientId));
            } else {
                setDeletedDietIds(prev => prev.filter(id => id !== targetId));
                toast.error(result?.error || 'Failed to delete diet plan');
            }
        }
    };

    return (
        <div className="card p-3 mb-4 shadow-sm border-0" style={{ borderRadius: '12px' }}>
            {/* Header & Actions */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div>
                    <h5 className="mb-0 fw-bold text-dark">
                        <i className="fa fa-utensils text-success me-2"></i> Diet & Nutrition Charts
                    </h5>
                    <small className="text-muted">
                        Prescribe structured digital meal schedules or upload external diet charts
                    </small>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button
                        className="btn btn-sm btn-success text-white fw-semibold"
                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                        onClick={() => {
                            resetForms();
                            setShowCreateModal(true);
                        }}
                    >
                        <i className="fa fa-plus me-1"></i> Create Digital Diet
                    </button>
                    <button
                        className="btn btn-sm btn-outline-success fw-semibold"
                        onClick={() => {
                            resetForms();
                            setShowUploadModal(true);
                        }}
                    >
                        <i className="fa fa-upload me-1"></i> Upload Diet File
                    </button>
                </div>
            </div>

            {/* List of Diets */}
            {loading && displayedDiets.length === 0 ? (
                <div className="text-center py-4 text-muted">
                    <i className="fa fa-spinner fa-spin fa-2x mb-2 text-success"></i>
                    <div>Loading diet charts...</div>
                </div>
            ) : displayedDiets.length === 0 ? (
                <div className="text-center py-4 bg-light rounded text-muted">
                    <i className="fa fa-clipboard-list fa-2x mb-2 text-secondary"></i>
                    <p className="mb-2">No diet charts prescribed yet for this patient.</p>
                    <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => {
                            resetForms();
                            setShowCreateModal(true);
                        }}
                    >
                        + Prescribe First Diet Plan
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '15%' }}>Date</th>
                                <th style={{ width: '30%' }}>Diet Plan & Condition</th>
                                <th style={{ width: '18%' }}>Prescribed By</th>
                                <th style={{ width: '12%' }}>Type</th>
                                <th className="text-end" style={{ width: '25%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedDiets.map((dietItem) => {
                                const isDigital = dietItem.dietType === 'digital';
                                return (
                                    <tr key={dietItem._id || dietItem.id}>
                                        <td className="fw-semibold">
                                            {formatDate(dietItem.date)}
                                        </td>
                                        <td>
                                            <div className="fw-bold text-dark">{dietItem.title}</div>
                                            {dietItem.condition && (
                                                <small className="text-muted">
                                                    Condition: {dietItem.condition}
                                                </small>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                <i className="fa fa-user-md text-success me-1"></i>
                                                {dietItem.doctor || 'Dr. Consultant'}
                                            </span>
                                        </td>
                                        <td>
                                            {isDigital ? (
                                                <span className="badge bg-success bg-opacity-75 text-white">
                                                    <i className="fa fa-clipboard-check me-1"></i> Digital Plan
                                                </span>
                                            ) : (
                                                <span className="badge bg-warning bg-opacity-75 text-dark">
                                                    <i className={`fa ${dietItem.fileType === 'pdf' ? 'fa-file-pdf' : 'fa-image'} me-1`}></i>
                                                    Uploaded File
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group btn-group-sm">
                                                {/* View Modal */}
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    title="View Diet Plan"
                                                    onClick={() => setPreviewDiet(dietItem)}
                                                >
                                                    <i className="fa fa-eye"></i>
                                                </button>

                                                {/* Edit Diet Plan */}
                                                {isDigital && (
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        title="Edit Diet Plan"
                                                        onClick={() => handleEditDiet(dietItem)}
                                                    >
                                                        <i className="fa fa-pencil"></i>
                                                    </button>
                                                )}

                                                {/* Download PDF / File */}
                                                {isDigital ? (
                                                    <>
                                                        <button
                                                            className="btn btn-outline-success"
                                                            title="Download English PDF"
                                                            onClick={() => downloadDietPDF(dietItem, patient, 'en')}
                                                        >
                                                            <i className="fa fa-download me-1"></i>EN
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-success"
                                                            title="Download Tamil PDF (with English patient details)"
                                                            onClick={() => handleDownloadTamil(dietItem)}
                                                        >
                                                            <i className="fa fa-language me-1"></i>தமிழ்
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-dark"
                                                            title="Print / Save as PDF"
                                                            onClick={() => handlePrintDiet(dietItem, 'en')}
                                                        >
                                                            <i className="fa fa-print"></i>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <a
                                                        href={dietItem.fileUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn btn-outline-success"
                                                        title="View / Download File"
                                                    >
                                                        <i className="fa fa-download"></i>
                                                    </a>
                                                )}

                                                {/* WhatsApp */}
                                                <div className="btn-group btn-group-sm position-relative">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-success"
                                                        style={{ color: '#25D366' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const id = dietItem._id || dietItem.id;
                                                            setActiveWhatsAppId(activeWhatsAppId === id ? null : id);
                                                        }}
                                                        title="Send via WhatsApp"
                                                    >
                                                        <i className="fab fa-whatsapp"></i>
                                                    </button>
                                                    {activeWhatsAppId === (dietItem._id || dietItem.id) && (
                                                        <ul className="dropdown-menu dropdown-menu-end shadow-sm show d-block" style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1050, minWidth: '175px' }}>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="dropdown-item small py-2"
                                                                    onClick={() => {
                                                                        setActiveWhatsAppId(null);
                                                                        handleSendWhatsApp(dietItem, 'en');
                                                                    }}
                                                                >
                                                                    <i className="fa fa-globe text-primary me-2"></i> WhatsApp (English)
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    className="dropdown-item small py-2"
                                                                    onClick={() => {
                                                                        setActiveWhatsAppId(null);
                                                                        handleSendWhatsApp(dietItem, 'ta');
                                                                    }}
                                                                >
                                                                    <i className="fa fa-language text-success me-2"></i> WhatsApp (தமிழ்)
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    )}
                                                </div>

                                                {/* Delete */}
                                                <button
                                                    className="btn btn-outline-danger"
                                                    title="Delete Diet Plan"
                                                    onClick={() => setDeleteModal({
                                                        show: true,
                                                        id: dietItem._id || dietItem.id,
                                                        name: dietItem.title
                                                    })}
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ======================================================== */}
            {/* 🔹 CREATE DIGITAL DIET MODAL                              */}
            {/* ======================================================== */}
            {showCreateModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                    <h5 className="modal-title fw-bold">
                                        <i className={`fa ${editingDiet ? 'fa-pencil-alt' : 'fa-clipboard-list'} me-2`}></i>
                                        {editingDiet ? `Edit Diet Plan for ${patient.name}` : `Create Digital Diet Plan for ${patient.name}`}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowCreateModal(false)}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 bg-light">
                                    {/* Patient Banner */}
                                    <div className="card p-3 mb-3 border-0 shadow-sm" style={{ backgroundColor: '#e8f5e9' }}>
                                        <div className="row g-2 align-items-center">
                                            <div className="col-12 col-md-6">
                                                <span className="text-muted small">Prescribing For:</span>{' '}
                                                <strong className="text-dark fs-6">{patient.name}</strong>{' '}
                                                <span className="badge bg-success ms-2">{patient.patientId || `ID: ${patient._id}`}</span>
                                            </div>
                                            <div className="col-12 col-md-6 text-md-end text-muted small">
                                                Age: <strong>{patient.age || 'N/A'} Yrs</strong> | Gender: <strong>{patient.gender || 'N/A'}</strong> | Phone: <strong>{patient.phone || 'N/A'}</strong>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 1. MASTER TEMPLATE SELECTOR & PAST DIETS SEARCH */}
                                    <div className="card p-3 mb-3 border-0 shadow-sm">
                                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                            <label className="form-label mb-0 fw-bold text-success">
                                                <i className="fa fa-book-medical me-1"></i> Choose from Common Library OR Search Past Diets
                                            </label>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success fw-semibold"
                                                    onClick={() => {
                                                        setShowSearchPastModal(true);
                                                        if (allPatientDiets.length === 0) dispatch(getAllPatientDiets());
                                                    }}
                                                >
                                                    <i className="fa fa-search me-1"></i> Search Past Prescribed Diets
                                                </button>
                                                <Link to="/admin/diets" target="_blank" className="text-decoration-none small text-success fw-semibold align-self-center">
                                                    <i className="fa fa-external-link-alt me-1"></i> All Diets
                                                </Link>
                                            </div>
                                        </div>
                                        <div className="row g-2 align-items-center">
                                            <div className={selectedTemplateId ? "col-12 col-md-9" : "col-12"}>
                                                <select
                                                    className="form-select"
                                                    value={selectedTemplateId}
                                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                                >
                                                    <option value="">
                                                        -- Put diet from scratch or choose from library --
                                                    </option>
                                                    {availableTemplates.length > 0 && (
                                                        <optgroup label="Clinic Master Templates">
                                                            {availableTemplates.map(tmpl => (
                                                                <option key={`t-${tmpl._id || tmpl.id}`} value={`template:${tmpl._id || tmpl.id}`}>
                                                                    {tmpl.name || tmpl.title} {tmpl.condition ? `(${tmpl.condition})` : ''}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                    {allPatientDiets && allPatientDiets.length > 0 && (
                                                        <optgroup label="Past Prescribed Patient Diets (Reuse)">
                                                            {allPatientDiets.filter(d => !deletedDietIds.includes(String(d._id || d.id))).slice(0, 50).map(d => (
                                                                <option key={`p-${d._id || d.id}`} value={`past:${d._id || d.id}`}>
                                                                    {d.title} {d.condition ? `(${d.condition})` : ''} {d.patient?.name ? `— for ${d.patient.name}` : ''}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                </select>
                                            </div>
                                            {selectedTemplateId && selectedTemplateId.startsWith('template:') && (
                                                <div className="col-12 col-md-3">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm w-100 fw-semibold"
                                                        title="Delete this template from Common Diet Library"
                                                        onClick={() => {
                                                            const rawId = selectedTemplateId.replace('template:', '');
                                                            const found = templates.find(t => String(t._id || t.id) === String(rawId));
                                                            setDeleteTemplateModal({
                                                                show: true,
                                                                id: rawId,
                                                                name: found?.name || found?.title || 'Selected Common Diet'
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-trash me-1"></i> Delete Template
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {selectedTemplateId && (
                                            <small className="text-success mt-2 d-block">
                                                <i className="fa fa-check-circle me-1"></i>
                                                Preset loaded! You can now adjust timings, change foods, or add/remove slots specifically for {patient.name}.
                                            </small>
                                        )}
                                    </div>

                                    {/* 2. PRESCRIPTION META DETAILS */}
                                    <div className="card p-3 mb-3 border-0 shadow-sm">
                                        <div className="row g-3">
                                            <div className="col-12 col-md-4">
                                                <label className="form-label small fw-bold text-muted">Diet Plan Title *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. Weight Loss Diet, Diabetic Naturopathy Diet"
                                                    value={dietTitle}
                                                    onChange={(e) => setDietTitle(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-12 col-md-3">
                                                <label className="form-label small fw-bold text-muted">Health Condition</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="e.g. Obesity, Type 2 Diabetes"
                                                    value={condition}
                                                    onChange={(e) => setCondition(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-12 col-md-3">
                                                <label className="form-label small fw-bold text-muted">Consulting Doctor *</label>
                                                <select
                                                    className="form-select"
                                                    value={doctor}
                                                    onChange={(e) => handleDoctorChange(e.target.value)}
                                                >
                                                    <option value="">-- Select Doctor --</option>
                                                    {doctors.length > 0 ? (
                                                        doctors.map(d => (
                                                            <option key={d._id || d.id} value={d.name}>
                                                                {d.name.startsWith('Dr') ? d.name : `Dr. ${d.name}`} {d.qualification ? `(${d.qualification})` : (d.designation ? `(${d.designation})` : '(BNYS)')}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <option value={user?.name || 'Dr. Consultant'}>
                                                            {user?.name || 'Dr. Consultant'} (BNYS)
                                                        </option>
                                                    )}
                                                </select>
                                            </div>

                                            <div className="col-12 col-md-2">
                                                <label className="form-label small fw-bold text-muted">Prescribed Date</label>
                                                <AppDatePicker
                                                    label="Diet Date"
                                                    value={dietDate}
                                                    onChange={(val) => setDietDate(val)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 3. DYNAMIC MEAL SLOTS EDITOR */}
                                    <div className="card p-3 mb-3 border-0 shadow-sm">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                            <div>
                                                <h6 className="fw-bold mb-0 text-dark">
                                                    <i className="fa fa-clock text-primary me-2"></i> Meal Schedule & Timings
                                                </h6>
                                                <small className="text-muted">
                                                    <i className="fa fa-keyboard text-success me-1"></i>
                                                    Direct Tamil input supported. You can type in Tamil, English, or type in English and click "தமிழில் மாற்றுக" below.
                                                </small>
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success fw-semibold"
                                                    onClick={handleTranslateFormToTamil}
                                                    disabled={isTranslating}
                                                    title="Translate meal slot names, food items, and guidelines into Tamil"
                                                >
                                                    {isTranslating ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin me-1"></i> Translating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-language me-1"></i> தமிழில் மாற்றுக (Convert to Tamil)
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary fw-semibold"
                                                    onClick={handleAddSlot}
                                                >
                                                    <i className="fa fa-plus me-1"></i> Add Meal Slot
                                                </button>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-bordered align-middle">
                                                <thead className="table-light small">
                                                    <tr>
                                                        <th style={{ width: '18%' }}>Time (Click to Pick)</th>
                                                        <th style={{ width: '18%' }}>Slot Name</th>
                                                        <th style={{ width: '38%' }}>Recommended Foods & Nutrition</th>
                                                        <th style={{ width: '21%' }}>Instructions / Notes</th>
                                                        <th className="text-center" style={{ width: '5%' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {slots.map((slot, index) => (
                                                        <tr key={slot.id || index}>
                                                            <td>
                                                                <AppTimePicker
                                                                    value={slot.time}
                                                                    placeholder="Select Time"
                                                                    onChange={(val) => handleSlotChange(index, 'time', val)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="e.g. Breakfast"
                                                                    value={slot.title}
                                                                    onChange={(e) => handleSlotChange(index, 'title', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <textarea
                                                                    rows="2"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Food items, fruits, juices, vegetables..."
                                                                    value={slot.items}
                                                                    onChange={(e) => handleSlotChange(index, 'items', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="e.g. Chew well, sip slowly"
                                                                    value={slot.instructions}
                                                                    onChange={(e) => handleSlotChange(index, 'instructions', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger border-0"
                                                                    title="Remove slot"
                                                                    onClick={() => handleRemoveSlot(index)}
                                                                >
                                                                    <i className="fa fa-times"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* 4. GUIDELINES & DO'S AND DON'TS */}
                                    <div className="card p-3 mb-3 border-0 shadow-sm">
                                        <label className="form-label fw-bold text-dark mb-1">
                                            <i className="fa fa-lightbulb text-warning me-1"></i> Lifestyle Guidelines, Water Intake & Do's / Don'ts
                                        </label>
                                        <textarea
                                            rows="3"
                                            className="form-control"
                                            placeholder="• Drink 3L warm water daily\n• Avoid refined sugar & maida\n• 30 mins brisk walking or yoga"
                                            value={guidelines}
                                            onChange={(e) => setGuidelines(e.target.value)}
                                        />
                                    </div>

                                    {/* 5. SAVE AS REUSABLE PRESET CHECKBOX */}
                                    <div className="card p-3 border-0 shadow-sm">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="saveAsTemplateCheck"
                                                checked={saveAsTemplate}
                                                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                                            />
                                            <label className="form-check-label fw-semibold text-dark" htmlFor="saveAsTemplateCheck">
                                                Save this diet as a new reusable template in the Common Library
                                            </label>
                                        </div>
                                        {saveAsTemplate && (
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Template Name (e.g. Weight Loss Plan V2)"
                                                    value={newTemplateName}
                                                    onChange={(e) => setNewTemplateName(e.target.value)}
                                                />
                                                <small className="text-muted">
                                                    This template will be saved without patient details so it can be applied to any other patient in the future.
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer bg-white border-0 py-3">
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success fw-semibold px-4"
                                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                        onClick={handleSaveDigitalDiet}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin me-2"></i> {editingDiet ? 'Updating...' : 'Saving...'}
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-check me-2"></i> {editingDiet ? 'Update Diet Plan' : 'Save & Prescribe Diet'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ======================================================== */}
            {/* 🔹 SEARCH PAST PRESCRIBED DIETS MODAL                    */}
            {/* ======================================================== */}
            {showSearchPastModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1065, background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                    <div>
                                        <h5 className="modal-title fw-bold mb-0">
                                            <i className="fa fa-search me-2"></i> Search Past Prescribed Diets
                                        </h5>
                                        <small className="opacity-75">
                                            Find previous diets by condition, title, or food item without needing the patient name
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowSearchPastModal(false)}
                                    ></button>
                                </div>

                                <div className="modal-body p-3 bg-light">
                                    <div className="input-group mb-3 shadow-sm">
                                        <span className="input-group-text bg-white border-end-0">
                                            <i className="fa fa-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0"
                                            placeholder="Search by health condition (e.g. diabetes, pcod), diet title, or food item (e.g. oats, soup)..."
                                            value={pastSearchTerm}
                                            onChange={(e) => setPastSearchTerm(e.target.value)}
                                            autoFocus
                                        />
                                        {pastSearchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setPastSearchTerm('')}
                                            >
                                                <i className="fa fa-times"></i>
                                            </button>
                                        )}
                                    </div>

                                    {/* Results List */}
                                    {(() => {
                                        const q = pastSearchTerm.toLowerCase().trim();
                                        const matches = (allPatientDiets || []).filter(d => {
                                            if (deletedDietIds.includes(String(d._id || d.id))) return false;
                                            if (!q) return true;
                                            const matchTitle = Boolean(d.title && d.title.toLowerCase().includes(q));
                                            const matchCond = Boolean(d.condition && d.condition.toLowerCase().includes(q));
                                            const matchDoc = Boolean(d.doctor && d.doctor.toLowerCase().includes(q));
                                            const matchPatientName = Boolean(d.patient?.name && d.patient.name.toLowerCase().includes(q));
                                            const matchPatientId = Boolean(d.patient?.patientId && d.patient.patientId.toLowerCase().includes(q));
                                            const matchGuidelines = Boolean(d.guidelines && d.guidelines.toLowerCase().includes(q));
                                            let matchSlots = false;
                                            let slotList = d.slots;
                                            if (typeof slotList === 'string') {
                                                try { slotList = JSON.parse(slotList); } catch (e) { slotList = []; }
                                            }
                                            if (Array.isArray(slotList)) {
                                                matchSlots = slotList.some(s =>
                                                    (s.title && s.title.toLowerCase().includes(q)) ||
                                                    (s.items && s.items.toLowerCase().includes(q))
                                                );
                                            }
                                            return matchTitle || matchCond || matchDoc || matchPatientName || matchPatientId || matchGuidelines || matchSlots;
                                        });

                                        if (matches.length === 0) {
                                            return (
                                                <div className="text-center py-4 bg-white rounded border">
                                                    <i className="fa fa-search fa-2x text-muted mb-2"></i>
                                                    <p className="text-muted small mb-0">No past diets found matching "{pastSearchTerm}".</p>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                                {matches.map((pd) => {
                                                    const pSlots = safeSlots(pd.slots);
                                                    return (
                                                        <div key={pd._id || pd.id} className="card border-0 shadow-sm p-3">
                                                            <div className="d-flex justify-content-between align-items-start gap-2">
                                                                <div>
                                                                    <h6 className="fw-bold text-dark mb-1">{pd.title}</h6>
                                                                    <div className="d-flex flex-wrap gap-1 align-items-center small mb-2">
                                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 py-1 px-2">
                                                                            <i className="fa fa-heartbeat me-1"></i>
                                                                            {pd.condition || 'General Naturopathy'}
                                                                        </span>
                                                                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 py-1 px-2">
                                                                            <i className="fa fa-user me-1"></i>
                                                                            {pd.patient?.name || 'Patient'}
                                                                        </span>
                                                                        <span className="badge bg-light text-muted border py-1 px-2">
                                                                            <i className="fa fa-clock me-1"></i> {pSlots.length} Slots
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-muted small" style={{ fontSize: '0.78rem' }}>
                                                                        {pSlots.slice(0, 2).map((s, idx) => (
                                                                            <span key={idx} className="me-2">
                                                                                <strong>{s.time || `Slot ${idx + 1}`}:</strong> {s.title ? `${s.title} - ` : ''}{s.items}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-success fw-semibold text-nowrap px-3"
                                                                    style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                                                    onClick={() => handleImportPastDiet(pd)}
                                                                >
                                                                    <i className="fa fa-check me-1"></i> Use This Plan
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="modal-footer bg-white border-0 py-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        onClick={() => setShowSearchPastModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ======================================================== */}
            {/* 🔹 UPLOAD EXTERNAL DIET FILE MODAL                       */}
            {/* ======================================================== */}
            {showUploadModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-md modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                    <h5 className="modal-title fw-bold">
                                        <i className="fa fa-file-upload me-2"></i> Upload Diet Chart (PDF / Image)
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setShowUploadModal(false)}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 bg-light">
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Diet Chart Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. External Nutrition Chart, Handwritten Diet Sheet"
                                            value={uploadTitle}
                                            onChange={(e) => setUploadTitle(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Consulting Doctor</label>
                                        <select
                                            className="form-select"
                                            value={uploadDoctor}
                                            onChange={(e) => setUploadDoctor(e.target.value)}
                                        >
                                            <option value="">-- Select Doctor --</option>
                                            {doctors.length > 0 ? (
                                                doctors.map(d => (
                                                    <option key={d._id || d.id} value={d.name}>{d.name}</option>
                                                ))
                                            ) : (
                                                <option value={user?.name || 'Dr. Consultant'}>
                                                    {user?.name || 'Dr. Consultant'}
                                                </option>
                                            )}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Prescribed Date</label>
                                        <AppDatePicker
                                            label="Date"
                                            value={uploadDate}
                                            onChange={(val) => setUploadDate(val)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Select File (PDF or Image) *</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="application/pdf,image/*"
                                            onChange={(e) => setUploadFile(e.target.files[0] || null)}
                                        />
                                        {uploadFile && (
                                            <small className="text-success mt-1 d-block">
                                                <i className="fa fa-file me-1"></i> {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                                            </small>
                                        )}
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label small fw-bold text-muted">Notes / Remarks</label>
                                        <textarea
                                            rows="2"
                                            className="form-control"
                                            placeholder="Optional notes about this document..."
                                            value={uploadNotes}
                                            onChange={(e) => setUploadNotes(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer bg-white border-0 py-3">
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        onClick={() => setShowUploadModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success fw-semibold px-4"
                                        style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                        onClick={handleSaveFileUpload}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin me-2"></i> Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-cloud-upload-alt me-2"></i> Upload & Save
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ======================================================== */}
            {/* 🔹 PREVIEW DIET DETAILS MODAL                            */}
            {/* ======================================================== */}
            {previewDiet && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055, background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
                                <div className="modal-header text-white" style={{ backgroundColor: '#1b4d3e' }}>
                                    <div>
                                        <h5 className="modal-title fw-bold mb-0">
                                            {previewDiet.title}
                                        </h5>
                                        <small className="text-white-50">
                                            Prescribed on {formatDate(previewDiet.date)} by {previewDiet.doctor || 'Dr. Consultant'}
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => setPreviewDiet(null)}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 bg-light">
                                    {previewDiet.dietType === 'digital' ? (
                                        <>
                                            <h6 className="fw-bold text-dark mb-3">Meal Schedule</h6>
                                            <div className="list-group mb-3">
                                                {safeSlots(previewDiet.slots).map((s, idx) => (
                                                    <div key={idx} className="list-group-item p-3 border rounded mb-2">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="badge bg-success">{s.time}</span>
                                                            <strong className="text-primary">{s.title}</strong>
                                                        </div>
                                                        <div className="fw-semibold text-dark mt-1" style={{ whiteSpace: 'pre-line' }}>
                                                            {s.items}
                                                        </div>
                                                        {s.instructions && (
                                                            <small className="text-muted d-block mt-1">
                                                                <i className="fa fa-info-circle me-1"></i> {s.instructions}
                                                            </small>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {previewDiet.guidelines && (
                                                <div className="p-3 rounded bg-white border">
                                                    <h6 className="fw-bold text-warning mb-1">
                                                        <i className="fa fa-check-circle me-1"></i> Guidelines & Tips
                                                    </h6>
                                                    <p className="small mb-0 text-dark" style={{ whiteSpace: 'pre-line' }}>
                                                        {previewDiet.guidelines}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-4 bg-white rounded border">
                                            <i className={`fa ${previewDiet.fileType === 'pdf' ? 'fa-file-pdf text-danger' : 'fa-image text-primary'} fa-3x mb-2`}></i>
                                            <h5>Attached File</h5>
                                            <p className="text-muted small mb-3">{previewDiet.notes || 'No notes added'}</p>
                                            <a
                                                href={previewDiet.fileUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-outline-success btn-sm"
                                            >
                                                <i className="fa fa-external-link-alt me-1"></i> Open File
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-footer bg-white border-0 py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div className="d-flex flex-wrap gap-2">
                                        {previewDiet.dietType === 'digital' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-success fw-semibold"
                                                    style={{ backgroundColor: '#1b4d3e', borderColor: '#1b4d3e' }}
                                                    onClick={() => downloadDietPDF(previewDiet, patient, 'en')}
                                                >
                                                    <i className="fa fa-download me-1"></i> English PDF
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-success fw-semibold"
                                                    onClick={() => handleDownloadTamil(previewDiet)}
                                                    disabled={downloadingPdf}
                                                >
                                                    {downloadingPdf ? (
                                                        <>
                                                            <i className="fa fa-spinner fa-spin me-1"></i> Generating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa fa-language me-1"></i> தமிழ் PDF (Tamil)
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-dark fw-semibold"
                                                    onClick={() => handlePrintDiet(previewDiet, 'en')}
                                                    title="Print or Save as PDF"
                                                >
                                                    <i className="fa fa-print me-1"></i> Print / Save as PDF
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-success fw-semibold"
                                            style={{ color: '#25D366', borderColor: '#25D366' }}
                                            onClick={() => handleSendWhatsApp(previewDiet, 'en')}
                                            title="Share English Diet link via WhatsApp"
                                        >
                                            <i className="fab fa-whatsapp me-1"></i> WhatsApp (EN)
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-success fw-semibold"
                                            style={{ color: '#25D366', borderColor: '#25D366' }}
                                            onClick={() => handleSendWhatsApp(previewDiet, 'ta')}
                                            title="Share Tamil Diet link via WhatsApp"
                                        >
                                            <i className="fab fa-whatsapp me-1"></i> WhatsApp (தமிழ்)
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            onClick={() => setPreviewDiet(null)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ======================================================== */}
            {/* 🔹 DELETE CONFIRM MODAL (Patient Diet)                    */}
            {/* ======================================================== */}
            <DeleteConfirmModal
                show={deleteModal.show}
                title="Move Diet Plan to Trash"
                itemName={deleteModal.name}
                actionType="delete"
                onClose={() => setDeleteModal({ show: false, id: null, name: '' })}
                onConfirm={confirmDeleteDiet}
            />

            {/* ======================================================== */}
            {/* 🔹 DELETE CONFIRM MODAL (Common Diet Template)           */}
            {/* ======================================================== */}
            <DeleteConfirmModal
                show={deleteTemplateModal.show}
                title="Delete Common Diet Plan"
                itemName={deleteTemplateModal.name}
                actionType="delete"
                onClose={() => setDeleteTemplateModal({ show: false, id: null, name: '' })}
                onConfirm={handleConfirmDeleteTemplate}
            />
        </div>
    );
}

